
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../Style/Report.css";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";

export default function Report() {
  const API_URL = "https://api.yellowmatrix.co.in";
  const navigate = useNavigate();
  const [analyticsData, setAnalyticsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState("24hours");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        let url = `${API_URL}/dashboard/vehicle-daily-stats`;
        const params = new URLSearchParams();

        if (range === "custom") {
          if (!startDate || !endDate) {
            setLoading(false);
            return;
          }

          if (startDate > endDate) {
            setLoading(false);
            return;
          }

          params.append("start", startDate);
          params.append("end", endDate);
        } else if (range !== "24hours") {
          params.append("range", range);
        }

        if (vehicleId) {
          params.append("vehicleid", vehicleId);
        }

        const finalUrl =
          params.toString().length > 0 ? `${url}?${params.toString()}` : url;
        const response = await fetch(finalUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        const data = await response.json();
        setAnalyticsData(data.data || []);
      } catch (error) {
        console.error("API Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [range, startDate, endDate, vehicleId]);
  const chartData = React.useMemo(() => {
    const grouped = {};

    (analyticsData || []).forEach((item) => {
      const date = item.date;
      if (!grouped[date]) {
        grouped[date] = {
          name: date,
          distance: 0,
          runtime: 0,
          speed_sum: 0,
          speed_count: 0,
          idle: 0,
        };
      }

      grouped[date].distance += item.distance || 0;
      grouped[date].runtime += item.runtime || 0;
      grouped[date].idle += item.idle || 0;
      grouped[date].speed_sum += Number(item.avg_speed) || 0;
      grouped[date].speed_count += 1;
    });
    return Object.values(grouped)
      .sort((a, b) => new Date(a.name) - new Date(b.name))
      .map((entry) => ({
        name: new Date(entry.name).toLocaleDateString("en-GB"),
        distance: parseFloat(entry.distance.toFixed(2)),
        runtime: parseFloat(entry.runtime.toFixed(2)),
        idle: parseFloat(entry.idle.toFixed(2)),
        speed:
          entry.speed_count > 0
            ? parseFloat((entry.speed_sum / entry.speed_count).toFixed(2))
            : 0,
      }));
  }, [analyticsData]);
  const openReport = (id) => {
    navigate(`/report/${id}`);
  };
  const reports = [
    {
      id: "poi",
      img: "/Report_icon/AOI IN and OUT Report.png",
      title: "POI Report",
    },
    {
      id: "consolidated",
      img: "/Report_icon/Consolidated Report.png",
      title: "Consolidated Report",
    },
    {
      id: "summary",
      img: "/Report_icon/Current Summary Report.png",
      title: "Todays Summary",
    },
    { id: "can", img: "/Report_icon/J1939 Report.png", title: "CAN Report" },
    {
      id: "obd",
      img: "/Report_icon/OBD II Report.png",
      title: "OBD II Report",
    },
    {
      id: "alert",
      img: "/Report_icon/Overspeed Report.png",
      title: "Alert Report",
    },
    {
      id: "panic",
      img: "/Report_icon/Panic Report.png",
      title: "Panic Report",
    },
    { id: "trip", img: "/Report_icon/Trip Report.png", title: "Trip Report" },
  ];

  const customReports = [
    { name: "Custom Fleet Report" },
    { name: "Daily Kms Travelled" },
    { name: "Distance Report" },
    { name: "District Name" },
  ];

  return (
    <div className="container-report">
      <h3 className="report-h3">Analytics</h3>
      <p className="subtitle-report">
        Overview of vehicle activity and performance insights.
      </p>
      <hr className="line" />
      <div className="filter-container">
        <select
          value={range}
          onChange={(e) => setRange(e.target.value)}
          className="filter-select"
        >
          <option value="24hours">Last 24 Hours</option>
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="7days">Last 7 Days</option>
          <option value="custom">Custom Range</option>
        </select>
        <input
          type="text"
          placeholder="Vehicle ID"
          value={vehicleId}
          onChange={(e) => setVehicleId(e.target.value)}
          className="filter-input"
        />
        <button
          className="filter-clear-btn"
          onClick={() => {
            setRange("24hours");
            setStartDate("");
            setEndDate("");
            setVehicleId("");
          }}
        >
          Clear
        </button>
      </div>
      {range === "custom" && (
        <div className="filter-container">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="filter-input"
          />

          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="filter-input"
          />
        </div>
      )}
      {loading && <p>Loading data...</p>}
      <div className="analytics-grid">
        <div className="analytics-card">
          <h4>DISTANCE (km)</h4>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis dataKey="name" tick={{ fontSize: 12 }} />

              <YAxis tick={{ fontSize: 12 }} />

              <Tooltip formatter={(value) => `${value} km`} />

              <Legend />

              <Bar
                dataKey="distance"
                fill="#2ec4b6"
                radius={[6, 6, 0, 0]}
                animationDuration={500}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="analytics-card">
          <h4>RUN TIME (min)</h4>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value) => `${value} min`} />
              <Legend />
              <Line
                type="monotone"
                dataKey="runtime"
                stroke="#4caf50"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="analytics-card">
          <h4>AVERAGE SPEED (km/hr)</h4>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value) => `${value} km/h`} />
              <Legend />
              <Line
                type="monotone"
                dataKey="speed"
                stroke="#ff9800"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="analytics-card">
          <h4>IDLE TIME (min)</h4>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value) => `${value} min`} />
              <Legend />
              <Bar
                dataKey="idle"
                fill="#ffc107"
                radius={[6, 6, 0, 0]}
                animationDuration={500}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <h3 className="DefaultReports-h4">Reports</h3>
      <p className="subtitle-report">
        Centralized access to all operational and diagnostic vehicle reports.
      </p>

      <hr className="line" />

      <div className="grid-report">
        {reports.map((report) => (
          <div
            key={report.id}
            className="report-card"
            onClick={() => openReport(report.id)}
          >
            <img src={report.img} alt={report.title} />
            <p>{report.title}</p>
          </div>
        ))}
      </div>
      <hr className="line" />

      <div className="Custom-Reports">
        <h4 className="Customreports-h4">
          Custom Reports
          <button className="new-btn">+ NEW</button>
        </h4>

        <table className="table-Reports">
          <thead>
            <tr>
              <th>NAME</th>
              <th>ACTIONS</th>
            </tr>
          </thead>

          <tbody>
            {customReports.map((report, index) => (
              <tr key={index}>
                <td>
                  <a href="/">{report.name}</a>
                </td>
                <td>
                  <a href="/">Edit</a> | <a href="/">Delete</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
